/**
 * Change groups over the action log — what the fine-tune panel lists as
 * "one change", and the rules that keep undo coherent.
 *
 * A group is every content entry that targets the same node: the same base
 * node address, or the output of the same insert (an `insertParagraph` /
 * `insertClause` and every later entry anchored, via `{ fromSeq }`, inside
 * it). Within a group the latest effective entry is the HEAD — the version
 * the document shows. Earlier effective entries are SUPERSEDED: undoing one
 * would not show its predecessor, it would silently rewrite history under
 * the head. So:
 *
 *  - undo / restore / drop may only target the head of a group, the root of
 *    an insert group (removes the insert with everything inside it), or an
 *    entry the replay reports as a conflict (it is not in effect);
 *  - redo (undo of an undo) is only possible for the latest undo of a
 *    group, and only while nothing newer has been done in that group;
 *  - "Återställ original" is a batch of head-undos (`groupRevertTargets`).
 *
 * Pure: the log plus (optionally) one replay report. The report refines
 * conflicts — without it every effective entry counts as in effect.
 *
 * Own-field entries are not change groups (see `customFieldUsage` /
 * `deriveCustomFieldDefinitions`), but the same rules hold per field key:
 * only the latest effective entry of a key may be undone, only its latest
 * undo redone; and a field still referenced by effective content may not
 * lose its definition (`field_in_use`).
 */
import { formatLogAddress, formatNodeAddress } from './address.js';
import { customFieldUsage, foldCustomFields } from './fields.js';
import { applyRebase, dependsOn, foldLog, isContentEntry, isFieldEntry, isSuggestionDecision } from './fold.js';
const isInsert = (e) => e.op === 'insertParagraph' || e.op === 'insertClause' || e.op === 'insertRow';
function indexGroups(log, report) {
    const folded = foldLog(log);
    const entries = [...log.entries].sort((a, b) => a.seq - b.seq).filter((e) => !folded.malformed.has(e.seq));
    const bySeq = new Map(entries.map((e) => [e.seq, e]));
    const status = new Map((report ?? []).map((r) => [r.seq, r.status]));
    const groups = new Map();
    const groupOf = new Map();
    for (const e of entries) {
        // Suggestions join the change groups only once accepted.
        const suggestion = folded.suggestions.get(e.seq);
        if (suggestion && suggestion.state !== 'accepted')
            continue;
        if (isContentEntry(e)) {
            const { anchor } = applyRebase(e, folded.rebases.get(e.seq));
            const dep = dependsOn(anchor);
            let id = dep != null ? groupOf.get(dep) : undefined;
            if (!id)
                id = dep == null && isInsert(e) ? `insert:${e.seq}` : `node:${formatLogAddress(anchor)}`;
            groupOf.set(e.seq, id);
            let g = groups.get(id);
            if (!g) {
                const address = id.startsWith('insert:') ? formatNodeAddress({ fromPatch: String(e.seq) }) : formatLogAddress(anchor);
                g = {
                    groupId: id,
                    address,
                    rootSeq: e.seq,
                    seqs: [],
                    effectiveSeqs: [],
                    headSeq: null,
                    supersededSeqs: [],
                    actionSeqs: [],
                    redoSeq: null,
                    versions: 0,
                    status: 'reverted',
                    kind: 'edited',
                    lastSeq: e.seq,
                };
                groups.set(id, g);
            }
            g.seqs.push(e.seq);
            g.lastSeq = e.seq;
            if (!folded.undone.has(e.seq))
                g.effectiveSeqs.push(e.seq);
        }
        else if (!isFieldEntry(e)) {
            const id = groupOf.get(e.payload.targetSeq);
            if (!id)
                continue;
            groupOf.set(e.seq, id);
            const g = groups.get(id);
            g.actionSeqs.push(e.seq);
            g.lastSeq = e.seq;
        }
    }
    const conflicted = (seq) => status.get(seq) === 'conflict';
    for (const g of groups.values()) {
        const eff = g.effectiveSeqs;
        g.headSeq = eff.length > 0 ? eff[eff.length - 1] : null;
        g.versions = eff.length;
        g.supersededSeqs = eff.filter((seq) => supersededIn(g, seq, conflicted));
        g.status = eff.length === 0 ? 'reverted' : eff.some(conflicted) ? 'conflict' : 'applied';
        const root = bySeq.get(g.rootSeq);
        const last = bySeq.get(g.headSeq ?? g.seqs[g.seqs.length - 1]);
        g.kind = isInsert(root) ? 'inserted' : last.op === 'removeNode' || last.op === 'removeRow' ? 'removed' : last.op === 'moveSection' ? 'moved' : 'edited';
        const latestUndo = [...g.actionSeqs].reverse().find((seq) => {
            const u = bySeq.get(seq);
            if (u.op !== 'undo' || folded.undone.has(seq) || folded.invalid.has(seq))
                return false;
            const target = bySeq.get(u.payload.targetSeq);
            return !!target && isContentEntry(target);
        });
        if (latestUndo != null) {
            const u = bySeq.get(latestUndo);
            if (u.payload.targetSeq > (g.headSeq ?? 0))
                g.redoSeq = latestUndo;
        }
    }
    return { bySeq, groups, groupOf, undone: folded.undone, invalid: folded.invalid, malformed: folded.malformed, suggestions: folded.suggestions };
}
function supersededIn(g, seq, conflicted) {
    if (g.groupId === `insert:${seq}`)
        return false;
    if (conflicted(seq))
        return false;
    return g.effectiveSeqs.some((s) => s > seq && !conflicted(s));
}
/** The change groups of a log, in order of their first entry. */
export function groupChanges(log, report) {
    return [...indexGroups(log, report).groups.values()];
}
/**
 * Why an undo (restore / drop / redo) of `targetSeq` is refused, or `null`
 * when it is allowed. Pass the replay report of `log` when available:
 * entries in conflict are not in effect and may always be dropped.
 */
export function undoRefusal(log, targetSeq, report) {
    const idx = indexGroups(log, report);
    const target = idx.bySeq.get(targetSeq);
    if (!target || idx.invalid.has(targetSeq))
        return 'unknown_target';
    if (isSuggestionDecision(target))
        return 'suggestion_decision';
    const suggestion = idx.suggestions.get(targetSeq);
    if (suggestion && suggestion.state !== 'accepted')
        return 'open_suggestion';
    if (idx.undone.has(targetSeq))
        return 'already_undone';
    if (isFieldEntry(target))
        return fieldUndoRefusal(log, target.seq, idx);
    if (target.op === 'undo') {
        const inner = idx.bySeq.get(target.payload.targetSeq);
        if (inner && isFieldEntry(inner))
            return fieldRedoRefusal(log, target.seq, inner.seq, idx);
    }
    const g = idx.groups.get(idx.groupOf.get(targetSeq) ?? '');
    if (isContentEntry(target)) {
        return g?.supersededSeqs.includes(targetSeq) ? 'superseded_entry' : null;
    }
    if (target.op === 'undo') {
        const inner = idx.bySeq.get(target.payload.targetSeq);
        if (inner?.op === 'rebase')
            return null;
        return g && g.redoSeq === targetSeq ? null : 'stale_redo';
    }
    return null;
}
/**
 * The undo targets of "Återställ original" for a group, in append order:
 * the insert itself for an insert group (everything inside goes with it),
 * otherwise every effective entry, newest first — each is the head when
 * its undo is appended.
 */
export function groupRevertTargets(group) {
    if (group.effectiveSeqs.length === 0)
        return [];
    if (group.groupId === `insert:${group.rootSeq}` && group.effectiveSeqs.includes(group.rootSeq))
        return [group.rootSeq];
    return [...group.effectiveSeqs].reverse();
}
/** Field entries of one key, in seq order. */
function fieldEntriesOf(idx, key) {
    return [...idx.bySeq.values()].filter((e) => isFieldEntry(e) && e.payload.key === key);
}
const fieldKeyOf = (e) => e.payload.key;
/** Whether, with `seq` undone (or redone), the field's definition would go while content references it. */
function wouldOrphan(log, key, toggledSeq) {
    const usage = customFieldUsage(log).get(key) ?? [];
    if (usage.length === 0)
        return false;
    const probe = {
        ...log,
        entries: [...log.entries, { op: 'undo', seq: Number.MAX_SAFE_INTEGER, author: '', at: '', payload: { targetSeq: toggledSeq } }],
    };
    return !foldCustomFields(probe).fields.has(key);
}
function fieldUndoRefusal(log, seq, idx) {
    const target = idx.bySeq.get(seq);
    const key = fieldKeyOf(target);
    const later = fieldEntriesOf(idx, key).some((e) => e.seq > seq && !idx.undone.has(e.seq));
    if (later)
        return 'superseded_entry';
    return wouldOrphan(log, key, seq) ? 'field_in_use' : null;
}
function fieldRedoRefusal(log, undoSeq, innerSeq, idx) {
    const key = fieldKeyOf(idx.bySeq.get(innerSeq));
    const entries = fieldEntriesOf(idx, key).map((e) => e.seq);
    const keySeqs = new Set(entries);
    // The latest effective undo acting on this key's entries.
    const latestUndo = [...idx.bySeq.values()]
        .filter((e) => e.op === 'undo' && keySeqs.has(e.payload.targetSeq) && !idx.undone.has(e.seq) && !idx.invalid.has(e.seq))
        .reduce((max, e) => Math.max(max, e.seq), 0);
    const newer = entries.some((s) => s > innerSeq && !idx.undone.has(s));
    if (latestUndo !== undoSeq || newer)
        return 'stale_redo';
    return wouldOrphan(log, key, undoSeq) ? 'field_in_use' : null;
}
//# sourceMappingURL=groups.js.map